import { useState } from "react";
import { CreditCard, FileText, DollarSign, ArrowRight, X, Clock, CheckCircle2, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const OPTIONS = [
  { id: "po", label: "Purchase Order", icon: FileText, desc: "Agency pricing. Tax-exempt. Invoiced on net terms." },
  { id: "cc", label: "Credit Card", icon: CreditCard, desc: "Retail pricing + NYC sales tax. Needs approval from Fresh Fast Foods." },
  { id: "cash", label: "Cash", icon: DollarSign, desc: "Retail pricing + NYC sales tax. Needs approval from Fresh Fast Foods." },
];
const LABEL = { po: "Purchase Order", cc: "Credit Card", cash: "Cash" };

const StatusLine = ({ request }) => {
  if (!request || request.status === "used") return null;
  const map = {
    pending: { icon: Clock, cls: "bg-brand-gold/15 text-brand-gold-dark", text: `${LABEL[request.method]} request is waiting for approval. You can keep shopping at agency prices.` },
    approved: { icon: CheckCircle2, cls: "bg-brand-teal/10 text-brand-teal", text: `${LABEL[request.method]} approved for your next order.` },
    declined: { icon: X, cls: "bg-red-50 text-red-700", text: `${LABEL[request.method]} request was declined${request.decline_reason ? `: "${request.decline_reason}"` : "."}` },
  };
  const m = map[request.status];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <p data-testid="payment-request-status" className={`flex items-start gap-2 rounded-xl px-3 py-2.5 text-[12.5px] leading-snug ${m.cls}`}>
      <Icon className="h-4 w-4 shrink-0 mt-0.5" /> {m.text}
    </p>
  );
};

/**
 * Agency payment-method picker. Purchase Order applies instantly; card or
 * cash sends an approval request to Fresh Fast Foods (one order per approval).
 */
export default function PaymentMethodGate({ onClose, initialMethod = null }) {
  const { paymentChoice, setPaymentChoice, paymentRequest, requestPaymentMethod } = useCart();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [asking, setAsking] = useState(initialMethod);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const approved = paymentRequest?.status === "approved" ? paymentRequest.method : null;

  const pick = (id) => {
    if (id === "po" || isAdmin || id === approved) {
      setPaymentChoice(id);
      onClose?.();
      return;
    }
    setAsking(id);
  };

  const send = async () => {
    setSending(true);
    setError("");
    try {
      await requestPaymentMethod(asking, note.trim());
      setAsking(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" role="dialog" aria-modal="true" data-testid="payment-gate">
      <div className="w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="px-6 py-5 border-b border-black/5 flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-teal">Payment method</p>
            <h2 className="mt-1 font-display font-black text-[22px] leading-tight text-brand-charcoal">
              {asking ? `Request to pay by ${LABEL[asking]}` : "How will you be paying?"}
            </h2>
            <p className="mt-1.5 text-[13px] text-brand-charcoal/60 leading-snug">
              {asking
                ? "We'll review it and email you. Approval covers one order; after that your account goes back to Purchase Order."
                : "Agency accounts pay by Purchase Order by default. Card or cash needs our approval."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="ml-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-brand-charcoal/50 hover:bg-brand-charcoal/5 hover:text-brand-charcoal transition-colors shrink-0" aria-label="Close" data-testid="payment-gate-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3 overflow-y-auto">
          {!isAdmin && <StatusLine request={paymentRequest} />}
          {asking ? (
            <>
              <label className="block text-[12px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/70" htmlFor="payment-request-note">
                Reason (optional)
              </label>
              <textarea
                id="payment-request-note"
                data-testid="payment-request-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="e.g. Our PO system is down this month"
                className="w-full rounded-2xl border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-brand-teal"
              />
              {error && <p data-testid="payment-request-error" className="text-[12.5px] text-red-600">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setAsking(null)} data-testid="payment-request-back" className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-cream transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button type="button" onClick={send} disabled={sending} data-testid="payment-request-send" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors disabled:opacity-50">
                  {sending ? "Sending…" : "Send Request"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            OPTIONS.map(({ id, label, icon: Icon, desc }) => {
              const active = paymentChoice === id || (!paymentChoice && id === "po" && !isAdmin);
              const tag = id === "po" ? "Default" : isAdmin || approved === id ? "Approved" : paymentRequest?.status === "pending" && paymentRequest.method === id ? "Pending" : "Needs approval";
              return (
                <button key={id} type="button" onClick={() => pick(id)} data-testid={`payment-gate-${id}`} aria-pressed={active}
                  className={`w-full text-left rounded-2xl border-2 p-4 group transition-colors focus:outline-none focus:ring-2 focus:ring-brand-teal/30 ${active ? "border-brand-teal bg-brand-teal/5" : "border-black/10 hover:border-brand-teal hover:bg-brand-cream/40"}`}>
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-cream text-brand-charcoal shrink-0 group-hover:bg-brand-teal group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-black text-[16px] text-brand-charcoal">{label}</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-[0.1em] ${tag === "Needs approval" ? "bg-brand-gold/15 text-brand-gold-dark" : "bg-brand-teal/10 text-brand-teal"}`}>{tag}</span>
                      </div>
                      <p className="mt-1 text-[12.5px] text-brand-charcoal/65 leading-snug">{desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-brand-charcoal/30 mt-1.5 shrink-0 group-hover:text-brand-teal transition-colors" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
